<template>
  <div class="card card-body shadow-sm mt-0">
    <!-- Cabeçalho -->
    <div class="form-header d-flex justify-content-between align-items-center mb-3">
      <h4 class="form-title">
        {{ route.params.id ? 'Editar Material' : 'Novo Material' }}
      </h4>
      <button class="btn btn-secondary btn-sm" @click="voltar">Voltar</button>
    </div>

    <BaseForm
      :isFormValid="isFormValid"
      :isSubmitting="isSubmitting"
      @submit="salvarMaterial"
      @cancel="voltar"
    >
      <div class="container-fluid p-0">
        <!-- Linha 1 -->
        <div class="row g-3">
          <div class="col-md-5">
            <label for="titulo" class="form-label">Título <span class="required">*</span></label>
            <input
              id="titulo"
              type="text"
              class="form-control"
              v-model="material.titulo"
              placeholder="Digite o título"
              required
            />
          </div>

          <div class="col-md-6">
            <label for="descricao" class="form-label">Descrição <span class="required">*</span></label>
            <input
              id="descricao"
              type="text"
              class="form-control"
              v-model="material.descricao"
              placeholder="Digite a descrição"
              required
            />
          </div>

          <!-- ✅ Público com checkboxes (igual Videoaulas) -->
          <div class="col-md-3">
            <label class="form-label">Público <span class="required">*</span></label>

            <div class="publicoBox mt-1" :class="{ invalid: publicoErro }">
              <div class="publicoGrid">
                <label class="publicoOption">
                  <input
                    class="form-check-input m-0"
                    type="checkbox"
                    value="Instrutor"
                    v-model="material.publico"
                  />
                  <span>Instrutor</span>
                </label>

                <label class="publicoOption">
                  <input
                    class="form-check-input m-0"
                    type="checkbox"
                    value="Apoio"
                    v-model="material.publico"
                  />
                  <span>Apoio</span>
                </label>

                <label class="publicoOption">
                  <input
                    class="form-check-input m-0"
                    type="checkbox"
                    value="Diretoria"
                    v-model="material.publico"
                  />
                  <span>Diretoria</span>
                </label>
              </div>
            </div>

            <small v-if="publicoErro" class="text-danger d-block mt-1">
              Selecione ao menos 1 público.
            </small>
          </div>
        </div>

        <!-- Linha 2: Arquivo -->
        <div class="row g-3 mt-3">
          <div class="col-12">
            <label for="arquivo" class="form-label">Arquivo Anexo</label>

            <!-- Se já existe um arquivo no servidor (edição) ou um novo selecionado -->
            <div v-if="existingFile || file" class="d-flex align-items-center">
              <input
                type="text"
                class="form-control"
                :value="file ? file.name : existingFile.name"
                disabled
              />
              <i
                class="bi bi-trash-fill text-danger ms-2"
                role="button"
                title="Remover arquivo"
                @click="removeFile"
              ></i>
            </div>

            <!-- Só exibe o input de upload quando não houver arquivo algum -->
            <div v-else>
              <input
                id="arquivo"
                type="file"
                class="form-control"
                @change="onFileChange"
                accept=".pdf,.ppt,.pptx,.doc,.docx,.jpg,.jpeg,.png"
              />
              <small class="text-muted d-block mt-1">
                Tipos aceitos: PDF, PPT/PPTX, DOC/DOCX, JPG/PNG.
              </small>
            </div>

            <!-- Opcional: link para abrir o arquivo existente -->
            <div v-if="existingFile?.url" class="mt-2">
              <a :href="existingFile.url" target="_blank" rel="noopener" class="file-link">
                Abrir arquivo atual
              </a>
            </div>
          </div>
        </div>
      </div>
    </BaseForm>
  </div>
</template>

<script>
import { reactive, ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BaseForm from '@/components/Layout/BaseForm.vue'
import axios from '@/services/axios'

export default {
  components: { BaseForm },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { appContext } = getCurrentInstance()
    const userId = appContext.config.globalProperties.$globalState.userId

    // ✅ publico agora é array
    const material = reactive({
      id: null,
      titulo: '',
      descricao: '',
      publico: []
    })

    const existingFile = ref(null)
    const file = ref(null)
    const isSubmitting = ref(false)
    const shouldRemoveFile = ref(false)

    const publicoErro = computed(() => !material.publico || material.publico.length === 0)

    const isFormValid = computed(() =>
      material.titulo.trim() !== '' &&
      material.descricao.trim() !== '' &&
      Array.isArray(material.publico) &&
      material.publico.length > 0
    )

    const voltar = () => router.push('/ajuda/materiais')

    function onFileChange(e) {
      file.value = e.target.files?.[0] || null
      if (file.value) {
        // se escolheu novo arquivo, não é "remover", é "substituir"
        shouldRemoveFile.value = false
      }
    }

    function removeFile() {
      file.value = null
      existingFile.value = null
      shouldRemoveFile.value = true // sinaliza para o servidor
    }

    function normalizarPublico(p) {
      if (Array.isArray(p)) return p.map(x => String(x).trim()).filter(Boolean)
      if (typeof p === 'string') {
        const s = p.trim()
        if (!s) return []
        if (s.includes(',') || s.includes(';')) {
          return s.split(/[;,]/).map(x => x.trim()).filter(Boolean)
        }
        return [s]
      }
      return []
    }

    const salvarMaterial = async () => {
      if (!isFormValid.value || isSubmitting.value) return
      isSubmitting.value = true

      const formData = new FormData()
      formData.append('titulo', material.titulo)
      formData.append('descricao', material.descricao)

      // ✅ envia array. Como é multipart/form-data, enviamos em JSON string
      // (backend vai precisar parsear)
      formData.append('publico', JSON.stringify(material.publico))

      // manter regra: usuário que editou vira o usuario_id
      formData.append('usuario_id', userId)

      if (shouldRemoveFile.value) {
        formData.append('remover_arquivo', 'true')
      }
      if (file.value) {
        formData.append('material', file.value)
      }

      try {
        const method = material.id ? 'put' : 'post'
        const url = material.id ? `/ajuda/materiais/${material.id}` : '/ajuda/materiais'
        await axios[method](url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        voltar()
      } catch (error) {
        console.error('Erro ao salvar material:', error.response?.data || error)
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(async () => {
      const id = route.params.id
      if (id) {
        try {
          const { data } = await axios.get(`/ajuda/materiais/${id}`)
          Object.assign(material, data)

          // ✅ garante array no editar (caso backend devolva string legado)
          material.publico = normalizarPublico(data.publico)

          if (data.arquivo_nome && data.caminho) {
            existingFile.value = {
              name: data.arquivo_nome,
              url: data.caminho
            }
          }
        } catch (error) {
          console.error('Erro ao carregar material:', error)
        }
      }
    })

    return {
      route,
      material,
      file,
      existingFile,
      isSubmitting,
      isFormValid,
      publicoErro,
      onFileChange,
      removeFile,
      salvarMaterial,
      voltar
    }
  }
}
</script>

<style scoped>
.card {
  max-width: 90%;
  margin: 0 auto;
}
.required {
  color: red;
}
label.form-label {
  font-size: 0.7rem;
  padding: 0.0rem 0.2rem;
  margin-bottom: 0rem;
}

/* Link do arquivo */
.file-link {
  text-decoration: none;
}
.file-link:hover {
  text-decoration: underline;
}

/* Público (box igual Videoaulas) */
.publicoBox {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 8px 10px;
  min-height: 86px;
  background: #fff;
}
.publicoBox.invalid {
  border-color: rgba(220, 53, 69, 0.55); /* vermelho suave */
}

.publicoGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

@media (min-width: 768px) {
  .publicoGrid {
    grid-template-columns: 1fr 1fr;
  }
}

.publicoOption {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  line-height: 1;
}
.publicoOption:hover {
  background: rgba(0, 0, 0, 0.04);
}
</style>
